// Light generic table — dense rows, sticky header, tabular figures. A thin,
// typed wrapper over the ui/table primitives so list screens (browser runs,
// etc.) share one scannable table style instead of re-styling each time.

import type { ReactNode } from 'react'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export interface Column<T> {
  header: ReactNode
  cell: (row: T) => ReactNode
  /// Right-align numeric columns (paired with tabular-nums on the table).
  align?: 'left' | 'right'
  className?: string
}

export function DataTable<T>({
  columns,
  rows,
  getKey,
  onRowClick,
  empty,
  className,
  label = 'Table',
}: {
  columns: Column<T>[]
  rows: T[]
  getKey: (row: T, index: number) => string | number
  onRowClick?: (row: T) => void
  /// Rendered in place of the table body when there are no rows.
  empty?: ReactNode
  className?: string
  /// Accessible name for the scroll region (keyboard users can focus and scroll it).
  label?: string
}) {
  if (rows.length === 0 && empty) {
    return <>{empty}</>
  }
  return (
    // The wrapper is the only horizontal scroller, so it takes focus and a name.
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className={cn('overflow-x-auto rounded-lg border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
    >
      <table data-slot="table" className="w-full caption-bottom text-sm tabular-nums">
        <TableHeader className="sticky top-0 z-10 bg-card">
          <TableRow>
            {columns.map((c, i) => (
              <TableHead key={i} className={cn(c.align === 'right' && 'text-right', c.className)}>
                {c.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow
              key={getKey(row, ri)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              {...(onRowClick
                ? {
                    tabIndex: 0,
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') onRowClick(row)
                    },
                  }
                : {})}
              className={cn(onRowClick && 'cursor-pointer focus-visible:outline-none focus-visible:bg-muted')}
            >
              {columns.map((c, ci) => (
                <TableCell key={ci} className={cn(c.align === 'right' && 'text-right', c.className)}>
                  {c.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  )
}
